import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { APP_MODULES, filterVisibleModules } from "../config/appModules";
import { APP_VERSION } from "../lib/version";
import UserMenu from "../components/UserMenu";
import BranchSelector from "../components/BranchSelector";
import A2DashboardKPI from "../components/A2DashboardKPI";
import { Search, Bell, User as UserIcon, ArrowUpRight, Sparkles, X, HelpCircle } from "lucide-react";

/**
 * AppLauncher · Home v3.0 (noCRM-inspired)
 * ═══════════════════════════════════════════════════════════════════
 * Clean, playful dashboard-launcher:
 * - White surface + Corporate blue accents + Yellow CTAs
 * - Modules grouped by category as big rounded action cards
 * - Big search hero on top (as noCRM home)
 * ═══════════════════════════════════════════════════════════════════
 */

// Category color map · deep saturated tints for the corporate palette
const GROUP_STYLE = {
  "Gestión de Operación":       { color: "#0A3747", tint: "#E6EDEF", label: "Gestión de Operación" },
  "Métricas":                   { color: "#0E7490", tint: "#CFFAFE", label: "Métricas" },   // v101.179.0
  "Centro de Inteligencia":     { color: "#7C3AED", tint: "#EDE9FE", label: "Centro de Inteligencia" },
  "Administración de Usuarios": { color: "#0369A1", tint: "#E0F2FE", label: "Administración de Usuarios" },
  "Documentación y Sistema":    { color: "#0F766E", tint: "#CCFBF1", label: "Documentación y Sistema" },
  "Inventario y Materiales":    { color: "#B45309", tint: "#FEF3C7", label: "Inventario y Materiales" },
  "Gestión Geográfica":         { color: "#B91C1C", tint: "#FEE2E2", label: "Gestión Geográfica" },
  "Integración":                { color: "#6B7000", tint: "#FBFCE0", label: "Integración" },
  "Otros":                      { color: "#475569", tint: "#F1F5F9", label: "Otros módulos" },
};

// Search aliases · maps user query terms to related module keywords
const SEARCH_ALIASES = {
  agenda: ["calendario", "agendamiento", "programacion", "programar"],
  agendas: ["calendario", "agendamiento", "programacion"],
  agendar: ["calendario", "agendamiento"],
  servicio: ["orden", "ot", "trabajo", "infraestructura"],
  servicios: ["orden", "ot", "trabajo"],
  factura: ["prefacturacion", "facturacion"],
  facturacion: ["prefacturacion"],
  reporte: ["auditoria", "log"],
  reportes: ["auditoria", "log"],
  tecnico: ["cuadrilla", "personal", "usuarios"],
  tecnicos: ["cuadrilla", "personal", "usuarios", "calendario"],
  mapa: ["gps", "war room"],
  gps: ["mapa", "war room"],
  cliente: ["odoo", "orden"],
  clientes: ["odoo", "orden"],
  usuarios: ["personal", "roles"],
  usuario: ["personal", "roles"],
  materiales: ["inventario", "stock"],
  material: ["inventario", "stock"],
  stock: ["inventario"],
};

export default function AppLauncher() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "Alfa-Field Connect · Inicio";
  }, []);

  const visibleModules = useMemo(
    () => filterVisibleModules(APP_MODULES, user, hasPerm),
    [user, hasPerm]
  );

  const filteredModules = useMemo(() => {
    if (!query.trim()) return visibleModules;
    // Normalize diacritics so "orden" matches "Órdenes" and "agenda" matches "Agendamiento"
    const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const q = norm(query);
    const aliasHits = SEARCH_ALIASES[q] || [];
    const terms = [q, ...aliasHits.map(norm)];
    const match = (m) => {
      const hay = `${norm(m.name)} ${norm(m.tagline)} ${norm(m.id)} ${norm(m.group)}`;
      return terms.some((t) => hay.includes(t));
    };
    return visibleModules.filter(match);
  }, [query, visibleModules]);

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const m of filteredModules) {
      const key = m.group || "Otros";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m);
    }
    return groups;
  }, [filteredModules]);

  // Get first name for greeting
  const firstName = user?.name?.split(" ")[0] || "Operador";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--bg-app)" }}
      data-testid="app-launcher-page"
    >
      {/* ═════ TopBar ═════ */}
      <header
        className="sticky top-0 z-40"
        style={{
          background: "var(--bg-app)",
        }}
      >
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <img
              src="/logo-alfanet.png"
              alt="Alfanet"
              className="h-9 w-auto"
              draggable={false}
            />
            <div className="hidden sm:block leading-tight">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#94A3B8" }}>
                Telealfacom
              </div>
              <div className="text-[15px] font-bold" style={{ color: "#0A3747" }}>
                Alfa-Field Connect
              </div>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <BranchSelector />
            <button
              onClick={() => navigate("/admin/notificaciones")}
              className="neu-icon-btn"
              title="Notificaciones"
              data-testid="launcher-notifications-btn"
            >
              <Bell size={16} />
            </button>
            <button
              onClick={() => navigate("/perfil")}
              className="neu-btn hidden sm:inline-flex"
              data-testid="launcher-profile-btn"
            >
              <UserIcon size={14} />
              Perfil
            </button>
            <button
              onClick={() => navigate("/centro-documentacion")}
              className="neu-icon-btn"
              title="Centro de Documentación · Ayuda"
              aria-label="Abrir Centro de Documentación AlfaNet"
              data-testid="launcher-help-btn"
            >
              <HelpCircle size={16} />
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      {/* ═════ Hero Section ═════ */}
      <section className="max-w-[1600px] mx-auto px-6 pt-12 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-3xl"
        >
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-4"
            style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}
          >
            <Sparkles size={11} style={{ color: "#6B7000" }} />
            <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>
              {visibleModules.length} módulos disponibles
            </span>
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]"
            style={{ color: "#0A3747" }}
          >
            {greeting}, <span style={{ color: "#0A3747" }}>{firstName}</span>. 👋
            <br />
            <span style={{ color: "#475569", fontWeight: 500 }}>¿Qué vas a orquestar hoy?</span>
          </h1>
        </motion.div>

        {/* Big Search Bar — neumorphic */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mt-6 max-w-2xl"
        >
          <div className="relative neu-inset" style={{ padding: 4, borderRadius: 20 }}>
            <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" style={{ color: "#0A3747" }} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar módulo (órdenes, técnicos, agendas, reportes...)"
              className="w-full pl-14 pr-14 py-4 text-[15px] outline-none bg-transparent"
              style={{ color: "#0F172A", borderRadius: 18 }}
              data-testid="launcher-search"
              autoComplete="off"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 neu-icon-btn"
                style={{ width: 32, height: 32 }}
                data-testid="launcher-search-clear"
                aria-label="Limpiar búsqueda"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {query && (
            <div className="mt-2 text-xs" style={{ color: "#64748B" }}>
              {filteredModules.length} resultado{filteredModules.length === 1 ? "" : "s"} para <strong>&ldquo;{query}&rdquo;</strong>
            </div>
          )}
        </motion.div>

        {/* Motor A2 · KPIs de éxito y contador de rescate manual (v2.8.9.4)
             El interruptor ON/OFF vive en /admin/reglas-agentes (Cerebros) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="mt-6 max-w-4xl"
          data-testid="launcher-a2-kpi"
        >
          <A2DashboardKPI />
        </motion.div>
      </section>

      {/* ═════ Modules Grid — noCRM-style category sections ═════ */}
      <section className="max-w-[1600px] mx-auto px-6 pb-16" data-testid="app-launcher-grid">
        {filteredModules.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-6xl mb-3">🔍</div>
            <div className="text-lg font-semibold mb-1" style={{ color: "#0A3747" }}>
              No encontramos módulos con ese nombre
            </div>
            <p className="text-sm mb-5" style={{ color: "#64748B" }}>
              Intenta con otras palabras clave.
            </p>
            <button
              onClick={() => setQuery("")}
              className="px-5 py-2.5 rounded-xl font-semibold text-sm transition"
              style={{ background: "#0A3747", color: "#E0E621" }}
              data-testid="launcher-search-clear-empty"
            >
              Limpiar búsqueda
            </button>
          </div>
        ) : (
          <div className="space-y-10">
            {Array.from(grouped.entries()).map(([label, items], gi) => (
              <CategorySection
                key={label}
                label={label}
                style={GROUP_STYLE[label] || GROUP_STYLE.Otros}
                items={items}
                onOpen={(to) => navigate(to)}
                index={gi}
              />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="max-w-[1600px] mx-auto px-6 pb-8">
        <div
          className="flex items-center justify-between flex-wrap gap-2 text-xs pt-6"
          style={{ borderTop: "1px solid #E2E8F0", color: "#94A3B8" }}
        >
          <span>© {new Date().getFullYear()} Telealfacom · Alfa-Field Connect</span>
          <span className="font-mono">v{APP_VERSION}</span>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CategorySection · noCRM-style titled card group
// ─────────────────────────────────────────────────────────────────
function CategorySection({ label, style, items, onOpen, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 + index * 0.06 }}
    >
      {/* Section header */}
      <div className="flex items-center gap-3 mb-4">
        <span
          className="w-1.5 h-6 rounded-full"
          style={{ background: style.color }}
        />
        <h2 className="text-lg font-bold tracking-tight" style={{ color: "#0A3747" }}>
          {style.label || label}
        </h2>
        <span
          className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
          style={{ background: style.tint, color: style.color }}
        >
          {items.length} {items.length === 1 ? "módulo" : "módulos"}
        </span>
        <div className="flex-1 h-px" style={{ background: "#E2E8F0" }} />
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
        {items.map((mod, i) => (
          <ModuleCard
            key={mod.id}
            module={mod}
            style={style}
            onOpen={onOpen}
            index={i}
          />
        ))}
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ModuleCard · Big rounded action-oriented card (noCRM style)
// ─────────────────────────────────────────────────────────────────
function ModuleCard({ module, style, onOpen, index }) {
  const Icon = module.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.5), ease: [0.16, 1, 0.3, 1] }}
      className="relative"
    >
      <motion.button
        onClick={() => onOpen(module.to)}
        whileHover={{ y: -3, scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        className="group relative text-left neu-card neu-card-hover p-5 overflow-hidden w-full"
        style={{ minHeight: 168 }}
        data-testid={`app-tile-${module.id}`}
        title={module.name}
      >
        {/* Icon badge */}
        <div className="flex items-start justify-between mb-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
            style={{
              background: style.tint,
              color: style.color,
              boxShadow: "inset -2px -2px 5px rgba(255,255,255,0.9), inset 2px 2px 5px rgba(163,177,198,0.35)",
            }}
          >
            <Icon size={22} strokeWidth={2} />
          </div>
          <ArrowUpRight
            size={18}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ color: style.color }}
          />
        </div>

        <div className="text-[14px] font-bold leading-tight mb-1 line-clamp-2" style={{ color: "#0A3747" }}>
          {module.name}
        </div>
        {module.tagline && (
          <div className="text-[11.5px] leading-snug line-clamp-2" style={{ color: "#64748B" }}>
            {module.tagline}
          </div>
        )}
        <div
          className="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 group-hover:scale-x-100 transition-transform"
          style={{ background: "#E0E621" }}
        />
      </motion.button>
    </motion.div>
  );
}
